import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowRight,
  CalendarDays,
  CheckCheck,
  CheckCircle2,
  Clock,
  FileImage,
  MapPin,
  MessageSquarePlus,
  Navigation,
  Phone,
  Route,
  ShieldCheck,
  Truck,
  Users,
} from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { areaClass } from '../../../components/ui/form-classes';
import { byId } from '../../../core/assignment-rules';
import { DISPATCH_CARD_TITLE, buildTaskDispatchCard } from '../../../core/dispatch-card';
import { navigationLinks, telHref, whatsappHref } from '../../../core/field-links';
import { formatPhone } from '../../../core/phone';
import { contactOf, formatTaskDate, redactTask, taskTypeName } from '../../../core/task-filters';
import { crewMatesOf } from '../../../core/task-views';
import {
  addSoldierTaskUpdate,
  confirmTaskReceipt,
  getSoldierTaskProgress,
  markSoldierFinished,
} from '../../../db/task-store';
import { CREW_ROLE_LABELS, EXTERNAL_PERSON_TAG } from '../../../types/assignment';
import type { CrewRole, TaskTeam } from '../../../types/assignment';
import type { Contact } from '../../../types/contact';
import {
  SOLDIER_TASK_STATE_LABELS,
  SOLDIER_TASK_STATE_TONES,
  soldierTaskState,
} from '../../../types/execution';
import type { SoldierTaskProgress } from '../../../types/execution';
import { vehicleCallSign } from '../../../core/military-id';
import { VEHICLE_TYPE_LABELS } from '../../../types/fleet';
import type { Soldier, Vehicle } from '../../../types/fleet';
import { TASK_PRIORITY_LABELS, TASK_PRIORITY_TONES } from '../../../types/task';
import type { Task, TaskType } from '../../../types/task';
import type { TimelineEvent } from '../../../types/timeline';
import type { User } from '../../../types/user';
import { DispatchOrderCard } from '../DispatchOrderCard';
import { ShareDispatchButton } from '../ShareDispatchButton';
import { QuickStatusUpdateBar, TaskTimeline } from '../timeline';

interface SoldierTaskViewProps {
  user: User;
  task: Task;
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  /** The one team the soldier rides with — the router resolved it. */
  team: TaskTeam;
  soldierId: string;
  crewRole: CrewRole;
  /** The feed of this vehicle's own steps — the router filtered the convoy out. */
  timelineEvents: readonly TimelineEvent[];
  /** `UPDATE_TASK_STATUS`, scoped to this task by the permissions engine. */
  canReport: boolean;
  onBack: () => void;
  onReload: () => void | Promise<void>;
}

/** A full-width field button — sized for a gloved thumb, not a mouse. */
function BigAction({
  href,
  onClick,
  disabled,
  tone = 'neutral',
  icon,
  children,
}: {
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'neutral' | 'brand' | 'success';
  icon: ReactNode;
  children: ReactNode;
}) {
  const tones = {
    neutral: 'border-line bg-panel text-ink hover:border-brand-300',
    brand: 'border-brand-500 bg-brand-500 text-white hover:bg-brand-700',
    success: 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700',
  } as const;
  const shell = [
    'w-full h-14 rounded-2xl border text-base font-semibold',
    'flex items-center justify-center gap-2 px-4',
    disabled ? 'opacity-50 pointer-events-none' : '',
    tones[tone],
  ].join(' ');

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={shell}>
        {icon}
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={shell}>
      {icon}
      {children}
    </button>
  );
}

function Card({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-line bg-panel p-4 space-y-3 text-right">
      <h3 className="text-sm font-bold text-ink/70 flex items-center gap-2">
        <span className="text-brand-500" aria-hidden>
          {icon}
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** One crew line, with a one-tap call when a number is known. */
function CrewLine({ label, name, phone }: { label: string; name?: string; phone?: string }) {
  const tel = telHref(phone);
  return (
    <div className="flex items-center justify-between gap-2 border-b border-line/60 last:border-0 py-2">
      <div className="min-w-0">
        <p className="text-xs text-ink/50">{label}</p>
        <p className="font-medium truncate">{name || 'טרם נקבע'}</p>
      </div>
      {tel ? (
        <a
          href={tel}
          className="shrink-0 h-10 px-3 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 text-sm font-medium flex items-center gap-1.5"
        >
          <Phone size={15} aria-hidden />
          חיוג
        </a>
      ) : null}
    </div>
  );
}

/**
 * The soldier's field view: one screen, one vehicle, one team slot and the
 * four actions a phone in the field is actually used for.
 *
 * What is *not* here is the point of the view. The task is redacted through
 * `redactTask()`, so internal manager notes never reach it; the router hands
 * over exactly one team, so the rest of the convoy is unreachable; and nothing
 * on the screen can change a crew — only report on it.
 */
export function SoldierTaskView({
  user,
  task,
  taskTypes,
  contacts,
  vehicles,
  soldiers,
  team,
  soldierId,
  crewRole,
  timelineEvents,
  canReport,
  onBack,
  onReload,
}: SoldierTaskViewProps) {
  // Manager-only notes are dropped before this screen ever sees the task.
  const visible = useMemo(() => redactTask(user, task), [user, task]);
  const [progress, setProgress] = useState<SoldierTaskProgress | null>(null);
  const [drawer, setDrawer] = useState<'update' | 'finish' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now] = useState(() => Date.now());

  const load = useCallback(async () => {
    setProgress(await getSoldierTaskProgress(task.id, soldierId));
  }, [task.id, soldierId]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const row = await getSoldierTaskProgress(task.id, soldierId);
      if (alive) setProgress(row);
    })();
    return () => {
      alive = false;
    };
  }, [task.id, soldierId]);

  /** Every report goes through the store, then the screen re-reads itself. */
  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      await onReload();
      setDrawer(null);
      setNote('');
    } catch (err) {
      setError(`הפעולה נכשלה — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const vehicle = byId(vehicles, team.vehicleId);
  const driver = byId(soldiers, team.driverId);
  const escort = byId(soldiers, team.escortId);
  const mates = crewMatesOf(team, soldierId);
  const contact = contactOf(visible, contacts);
  const navLinks = navigationLinks(visible);
  const state = soldierTaskState(progress);
  const typeName = taskTypeName(visible.taskTypeId, taskTypes);
  // The order for this vehicle alone — the router handed over one team, and the
  // shared image is built from exactly that, redacted task included.
  const dispatchCard = useMemo(
    () =>
      buildTaskDispatchCard(visible, {
        teams: [team],
        vehicles,
        soldiers,
        contacts,
        taskTypes,
        now,
      }),
    [visible, team, vehicles, soldiers, contacts, taskTypes, now],
  );

  const call = contact?.phone ? telHref(contact.phone) : null;
  const whatsapp = contact?.phone
    ? whatsappHref(contact.phone, `שלום, מדובר במשימה "${visible.title}".`)
    : null;

  return (
    <div className="mx-auto w-full max-w-md space-y-3">
      <button
        type="button"
        onClick={onBack}
        className="h-10 px-3 rounded-xl border border-line text-sm text-ink/60 hover:text-ink flex items-center gap-1.5"
      >
        <ArrowRight size={16} aria-hidden />
        חזרה למשימות
      </button>

      {/* ── The task, in the largest type on the screen ── */}
      <header className="rounded-2xl border border-line bg-panel p-4 space-y-3 text-right">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-xl font-bold leading-tight">{visible.title}</h2>
            {typeName ? <p className="mt-1 text-sm text-ink/50">{typeName}</p> : null}
          </div>
          <span
            className={[
              'shrink-0 text-sm px-2.5 py-1 rounded-lg border font-bold',
              TASK_PRIORITY_TONES[visible.priority],
            ].join(' ')}
          >
            {TASK_PRIORITY_LABELS[visible.priority]}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-line bg-canvas p-2.5">
            <p className="text-xs text-ink/50 flex items-center gap-1.5">
              <CalendarDays size={13} aria-hidden />
              תאריך
            </p>
            <p className="text-lg font-bold">{formatTaskDate(visible.date, now)}</p>
          </div>
          <div className="rounded-xl border border-line bg-canvas p-2.5">
            <p className="text-xs text-ink/50 flex items-center gap-1.5">
              <Clock size={13} aria-hidden />
              שעות
            </p>
            <p data-dir="ltr" className="text-lg font-bold tabular-nums">
              {visible.startTime}
              {visible.estEndTime ? `–${visible.estEndTime}` : ''}
            </p>
          </div>
        </div>

        <span
          className={[
            'inline-block text-sm px-2.5 py-1 rounded-lg border font-medium',
            SOLDIER_TASK_STATE_TONES[state],
          ].join(' ')}
        >
          {SOLDIER_TASK_STATE_LABELS[state]}
        </span>

        {visible.description ? (
          <p className="text-sm text-ink/70 leading-relaxed">{visible.description}</p>
        ) : null}
        {visible.operationalNotes ? (
          <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            {visible.operationalNotes}
          </p>
        ) : null}
        {visible.destinationAddress ? (
          <p className="text-sm text-ink/70 flex items-start gap-2">
            <MapPin size={15} className="text-ink/40 mt-0.5 shrink-0" aria-hidden />
            <span>{visible.destinationAddress}</span>
          </p>
        ) : null}
      </header>

      {/* ── The order itself, portrait, ready to forward to the crew ── */}
      {dispatchCard ? (
        <Card title={DISPATCH_CARD_TITLE} icon={<FileImage size={16} />}>
          <DispatchOrderCard
            card={dispatchCard}
            actions={<ShareDispatchButton card={dispatchCard} size="lg" />}
          />
        </Card>
      ) : null}

      {/* ── The vehicle this soldier rides on, and no other ── */}
      <Card title="הרכב שלי" icon={<Truck size={16} />}>
        {vehicle ? (
          <div className="flex items-center gap-3">
            <span
              data-dir="ltr"
              title="מספר פנימי"
              className="shrink-0 rounded-2xl border-2 border-brand-500 bg-brand-50 px-4 py-2 text-3xl font-black text-brand-700 tabular-nums"
            >
              {vehicleCallSign(vehicle)}
            </span>
            <div className="min-w-0">
              <p className="font-bold truncate">{vehicle.name}</p>
              <p className="text-sm text-ink/60">{VEHICLE_TYPE_LABELS[vehicle.vehicleType]}</p>
              <p data-dir="ltr" className="text-sm text-ink/50 tabular-nums">
                {vehicle.plateNumber}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink/50">טרם נקבע רכב לצוות.</p>
        )}
      </Card>

      {/* ── The team slot: who rides with me, in which role ── */}
      <Card title="הצוות שלי" icon={<Users size={16} />}>
        <p className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg border border-brand-300 bg-brand-50 text-brand-700 font-semibold">
          <ShieldCheck size={14} aria-hidden />
          התפקיד שלי: {CREW_ROLE_LABELS[crewRole]}
        </p>
        <div>
          <CrewLine
            label={CREW_ROLE_LABELS.driver}
            name={driver?.fullName}
            phone={driver?.phone}
          />
          {team.escortOption === 'soldier' ? (
            <CrewLine
              label={CREW_ROLE_LABELS.escort}
              name={escort?.fullName}
              phone={escort?.phone}
            />
          ) : null}
          {mates.map((mate) => {
            const mateSoldier = byId(soldiers, mate.soldierId);
            return (
              <CrewLine
                key={mate.id}
                label={`${CREW_ROLE_LABELS.passenger}${
                  mate.externalPassenger ? ` (${EXTERNAL_PERSON_TAG})` : ''
                }`}
                name={mateSoldier?.fullName ?? mate.externalPassenger?.fullName}
                phone={mateSoldier?.phone ?? mate.externalPassenger?.phone}
              />
            );
          })}
        </div>
        {team.teamNote ? (
          <p className="rounded-xl border border-line bg-canvas p-2.5 text-sm text-ink/70">
            {team.teamNote}
          </p>
        ) : null}
      </Card>

      {/* ── One tap: call, WhatsApp, navigate ── */}
      <Card title="קשר וניווט" icon={<Navigation size={16} />}>
        {contact ? (
          <p className="text-sm text-ink/60">
            {contact.name}
            {contact.role ? ` · ${contact.role}` : ''}
            {contact.phone ? (
              <span data-dir="ltr" className="mr-2 tabular-nums">
                {formatPhone(contact.phone)}
              </span>
            ) : null}
          </p>
        ) : (
          <p className="text-sm text-ink/50">לא הוגדר איש קשר למשימה.</p>
        )}
        <div className="space-y-2">
          {call ? (
            <BigAction href={call} tone="brand" icon={<Phone size={19} aria-hidden />}>
              חיוג לאיש קשר
            </BigAction>
          ) : null}
          {whatsapp ? (
            <BigAction href={whatsapp} icon={<MessageSquarePlus size={19} aria-hidden />}>
              פתיחת וואטסאפ
            </BigAction>
          ) : null}
          {navLinks.map((link) => (
            <BigAction key={link.id} href={link.href} icon={<Navigation size={19} aria-hidden />}>
              {link.label}
            </BigAction>
          ))}
          {navLinks.length === 0 ? (
            <p className="text-sm text-ink/50">לא הוגדרה כתובת יעד לניווט.</p>
          ) : null}
        </div>
      </Card>

      {/* ── The three things a soldier reports ── */}
      <Card title="ביצוע המשימה" icon={<CheckCircle2 size={16} />}>
        {!canReport ? (
          <p className="text-sm text-ink/50">אין לך הרשאת עדכון סטטוס למשימה זו.</p>
        ) : (
          <div className="space-y-3">
            {/* One tap per step — the bar a driver uses without stopping. */}
            <QuickStatusUpdateBar
              user={user}
              task={visible}
              soldierId={soldierId}
              events={timelineEvents}
              disabled={busy}
              onReported={async () => {
                await load();
                await onReload();
              }}
            />

            <BigAction
              tone={progress?.receiptConfirmedAt ? 'neutral' : 'brand'}
              disabled={busy || Boolean(progress?.receiptConfirmedAt)}
              onClick={() => void run(() => confirmTaskReceipt(user, task.id, soldierId))}
              icon={<CheckCircle2 size={19} aria-hidden />}
            >
              {progress?.receiptConfirmedAt ? 'קבלת המשימה אושרה' : 'אישור קבלת משימה'}
            </BigAction>

            <BigAction
              disabled={busy}
              onClick={() => setDrawer('update')}
              icon={<MessageSquarePlus size={19} aria-hidden />}
            >
              עדכן סטטוס / הוסף הערה
            </BigAction>

            <BigAction
              tone="success"
              disabled={busy || Boolean(progress?.finishedAt)}
              onClick={() => setDrawer('finish')}
              icon={<CheckCheck size={19} aria-hidden />}
            >
              {progress?.finishedAt ? 'סומן סיום ביצוע' : 'סימון סיום ביצוע'}
            </BigAction>

            <p className="text-xs text-ink/40">
              סימון סיום ביצוע מדווח על סיום החלק שלך. סגירת המשימה עצמה נעשית על ידי מנהל העבודה.
            </p>
          </div>
        )}

        {progress && progress.updates.length > 0 ? (
          <ul className="space-y-1.5 pt-1">
            {progress.updates.map((update) => (
              <li key={update.id} className="rounded-xl border border-line bg-canvas p-2.5 text-sm">
                <span className="text-ink/70">{update.note || 'עדכון סטטוס'}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {error ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {error}
          </p>
        ) : null}
      </Card>

      {/* ── The feed of this vehicle's own steps, oldest first ── */}
      <Card title="מהלך המשימה" icon={<Route size={16} />}>
        <TaskTimeline
          events={timelineEvents}
          order="asc"
          now={now}
          emptyLabel="עדיין לא דווח שלב במשימה. כל עדכון שתשלח יופיע כאן."
        />
      </Card>

      {drawer ? (
        <Modal
          title={drawer === 'finish' ? 'סימון סיום ביצוע' : 'עדכון מהשטח'}
          subtitle={visible.title}
          onClose={() => {
            setDrawer(null);
            setNote('');
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setDrawer(null);
                  setNote('');
                }}
                className="h-11 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busy || (drawer === 'update' && !note.trim())}
                onClick={() =>
                  void run(() =>
                    drawer === 'finish'
                      ? markSoldierFinished(user, task.id, soldierId, note)
                      : addSoldierTaskUpdate(user, task.id, soldierId, note, 'in_progress'),
                  )
                }
                className={[
                  'h-11 px-5 rounded-xl text-sm font-semibold text-white',
                  drawer === 'finish' ? 'bg-emerald-600' : 'bg-brand-500',
                  busy || (drawer === 'update' && !note.trim()) ? 'opacity-50' : '',
                ].join(' ')}
              >
                {drawer === 'finish' ? 'סיימתי את הביצוע' : 'שליחת עדכון'}
              </button>
            </>
          }
        >
          <label className="block space-y-1.5">
            <span className="text-sm text-ink/60">
              {drawer === 'finish' ? 'הערת סיום (רשות)' : 'מה קורה בשטח?'}
            </span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              className={areaClass}
              placeholder={drawer === 'finish' ? 'לדוגמה: הפריקה הושלמה, חוזרים לבסיס.' : 'לדוגמה: יצאנו מהשער, מתעכבים 20 דקות.'}
            />
          </label>
          {drawer === 'update' ? (
            <p className="mt-2 text-xs text-ink/40">
              העדכון מסמן את המשימה כ"בביצוע" ונשמר ביומן המשימה עבור מנהל העבודה.
            </p>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
